<template>
  <footer v-if="identity" class="footer">
    <nav v-if="shouldShowPrivacyPolicy" class="footer-links">
      <span class="nav-link" @click="privacyPolicyModalStore.openModal()">
        {{ $t('footer.legal_notice') }}
      </span>
      <br />
    </nav>
    <span class="footer-links footer-links--main-website d-block d-md-none">
      <a :href="identity?.contact.website.url" class="text-white font-weight-light" target="_blank">
        {{ $t('footer.custom_website_link_label') }}
      </a>
    </span>
    <div class="footer-powered-by d-flex flex-column fs-5">
      <div v-dompurify-html="$t('footer.powered_by.title')" />
      <div class="d-flex flex-column">
        <span
          v-dompurify-html="
            $t('footer.powered_by.subtitle', { dealership: identity?.contact.organization })
          "
        />
        <span>{{ identity?.contact.address }}</span>
      </div>
    </div>
    <div class="footer-logo">
      &copy; {{ currentYear }}
      <Popper
        :content="$t('footer.infernal_logo_popover')"
        hover
        arrow
        class="fs-5 align-self-center"
        placement="bottom"
        theme="light"
      >
        <InfernalMediaLogo variant="dealership" class="ms-3" @click="openInfernalMedia()" />
      </Popper>
    </div>
  </footer>
  <Teleport to="body">
    <Component :is="localizedConsent" v-if="identity" />
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, PropType } from 'vue'
import InfernalMediaLogo from '~conciergeUiCommons/components/InfernalMediaLogo.vue'
import { IdentityData } from '~concierge/types/IdentityTypes'
import { usePrivacyPolicyModalStore } from '~conciergeUiCommons/store/privacyPolicyModalStore'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'

export default defineComponent({
  name: 'FooterSection',
  components: {
    InfernalMediaLogo,
    ConsentEn: defineAsyncComponent(() => import('~conciergeUiCommons/components/ConsentEn.vue')),
    Consent: defineAsyncComponent(() => import('~conciergeUiCommons/components/Consent.vue'))
  },
  data() {
    return {
      currentYear: new Date().getFullYear()
    }
  },
  setup() {
    const privacyPolicyModalStore = usePrivacyPolicyModalStore()
    return {
      privacyPolicyModalStore
    }
  },
  props: {
    identity: {
      type: Object as PropType<IdentityData | null>,
      required: false
    }
  },
  computed: {
    lang: () => {
      return useSubmissionFormStore().formLanguage
    },
    localizedConsent() {
      if (this.lang === 'en') {
        return 'ConsentEn'
      }
      return 'Consent'
    },
    shouldShowPrivacyPolicy(): boolean {
      return this.identity?.contact?.province === 'QC'
    }
  },
  methods: {
    openInfernalMedia() {
      const url = 'https://infernal.media'
      window.open(url, '_blank')
    }
  }
})
</script>

<style scoped lang="scss">
body.desktop .footer {
  border-bottom-left-radius: var(--vff-border-radius, 4px);
  border-bottom-right-radius: var(--vff-border-radius, 4px);
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: var(--dealer-footer-background);
  color: var(--dealer-footer-text);
  font-size: 1.1rem;
  flex-direction: column;
}

.footer-logo {
  display: flex;
  place-items: center;
}

.footer-logo svg {
  max-width: 70px;
  height: auto;
  cursor: pointer;
}

.footer-links {
  display: inline-flex;
  place-items: center;

  span,
  a {
    font-weight: 400;
    color: var(--footer-link-text-color, var(--dealer-footer-text, #fff));
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--footer-link-text-color-hover, var(--dealer-footer-text, #fff));
      text-decoration: underline;
    }
  }
}

.footer-powered-by {
  font-size: 1.5rem;
  padding: 0.3em;

  em {
    font-style: normal;
  }
}
</style>
